import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowRight, Check, ShieldCheck, Clock, Sparkles, ArrowLeft, Lock, PlayCircle, Bot, Cpu, HardDrive, Activity, ArrowUpRight, ChevronRight, Workflow, Wrench, X } from "lucide-react";
import SEOHead from "../components/SEOHead";
import { toast } from "sonner";

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [setupAssist, setSetupAssist] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}`)
      .then(({ data }) => setProduct(data))
      .catch(() => {
        // Fallback: use static product data when backend is unavailable
        const STATIC_PRODUCTS = {
          "max-os-cloud": {
            product_id: "max-os-cloud",
            name: "Max OS Cloud",
            card_title: null,
            tagline: "Live autonomous agent. No setup.",
            description: "USD-priced single-agent cloud tier with immediate activation, warm-pool deployment, lightweight tenant isolation, and setup that can start at $0 or promo onboarding from $199.",
            price: 297.00,
            setup_fee: 0.00,
            billing: "per month",
            features: [
              "Ready to work — no setup",
              "Cloud deployment included",
              "Live and operational 24/7",
              "Full management support",
            ],
            benefits: [
              "Zero infrastructure to manage",
              "Live within minutes of subscribing",
              "Automatic updates and uptime monitoring",
              "Cancel anytime",
            ],
            preview: "Hosted single-agent Max OS Cloud runtime with autoscaling, encrypted storage, and 24/7 incident response.",
            badge: null,
            contact_only: false,
            is_subscription: true,
            cta_label: "DEPLOY TO CLOUD",
          },
          "max-os-cloud-enterprise": {
            product_id: "max-os-cloud-enterprise",
            name: "Max OS Cloud Enterprise",
            card_title: "Multi-Agent Command Center",
            tagline: "An entire AI department, live in your cloud.",
            description: "Multi-agent orchestration, stronger tenant isolation, and dedicated onboarding for enterprise customers.",
            price: 899.00,
            setup_fee: 1490.00,
            billing: "per month",
            features: [
              "Multiple agents executing simultaneously",
              "Dedicated account manager",
              "Custom integrations",
              "White label options",
            ],
            benefits: [
              "Replace whole departments, not individual roles",
              "Dedicated solutions architect from day one",
              "Custom SLAs and on-premise options available",
              "White-label and reseller programs",
            ],
            preview: "Multi-agent control plane, planner/executor workflow, security review, and dedicated 24/7 support.",
            badge: "Enterprise",
            contact_only: false,
            is_subscription: true,
            cta_label: "DEPLOY ENTERPRISE",
          },
          "playbook": {
            product_id: "playbook",
            name: "Complete course + prompts",
            card_title: "Build your Max OS",
            tagline: "Complete online course + prompts to create your own Max OS",
            description: "Step-by-step program that takes you from zero to a fully working autonomous AI digital worker.",
            price: 49.00,
            billing: "one-time",
            features: [
              "Complete step-by-step setup guide to build your AI agent from zero",
              "VPS cloud OpenClaw setup instructions",
              "Plug & Execute prompts to automatically build your agent",
              "24/7 autonomous + integrations",
            ],
            benefits: [
              "Go from zero to a working agent in under 14 days",
              "30+ video lessons + downloadable prompt library",
              "Lifetime access — including future updates",
              "Works with Claude, GPT, Gemini and local models",
            ],
            preview: "Module 0 — Opening · Module 1 — Setup & VPS · Module 2 — Security · Module 3 — Identity · Module 4 — Memory · Module 5/6 — Integrations & Skills · Module 7 — Proactivity · Module 8 — Multi-agents · Module 9 — Immune System · Module 10 — Mission Control · Module 11 — Wrap-up · Module 12 — Skills, APIs & Tools · Extras",
            badge: null,
            contact_only: false,
            is_subscription: false,
            cta_label: "GET FULL COURSE + PROMPTS",
          },
          "max-os": {
            product_id: "max-os",
            name: "Max OS",
            card_title: null,
            tagline: "Pre-configured autonomous AI agent.",
            description: "Pre-built Max OS autonomous AI agent persona ready to handle tasks. Deploy locally or on your VPS.",
            price: 289.00,
            billing: "one-time",
            features: [
              "Pre-built autonomous AI agent persona",
              "Talk to your agent from your phone",
              "Proactive task handling",
              "Deploy locally or on VPS",
              "24/7 capable automation",
            ],
            benefits: [
              "Skip months of trial and error — start with a battle-tested agent",
              "Voice + text channel ready (Telegram, WhatsApp, Discord)",
              "Self-hosted on your VPS — your data stays yours",
              "Includes mission control templates",
            ],
            preview: "Includes the full Max OS agent bundle, identity templates (SOUL, USER, AGENTS), proactive heartbeat, integrations skills, and a deployment runbook.",
            badge: "Most Popular",
            contact_only: false,
            is_subscription: false,
            cta_label: "GET MAX OS AGENT",
          },
          "max-os-enterprise": {
            product_id: "max-os-enterprise",
            name: "Max OS Enterprise",
            card_title: "Enterprise AI Department",
            tagline: "An entire AI department, ready to deploy.",
            description: "Multi-agent orchestration, stronger tenant isolation, and dedicated onboarding for enterprise customers.",
            price: 879.00,
            billing: "per month",
            from_price: true,
            features: [
              "Unlimited agents executing simultaneously",
              "Dedicated account manager",
              "Custom integrations",
              "White label options",
            ],
            benefits: [
              "Replace whole departments, not individual roles",
              "Dedicated solutions architect from day one",
              "Custom SLAs and on-premise options available",
              "White-label and reseller programs",
            ],
            preview: "Multi-agent control plane, planner/executor workflow, security review, and dedicated 24/7 support.",
            badge: null,
            contact_only: false,
            is_subscription: true,
            cta_label: "CONTACT SALES",
          },
        };
        const fallback = STATIC_PRODUCTS[productId];
        setProduct(fallback || false);
      })
      .finally(() => setLoading(false));
  }, [productId]);

  const handleBuy = async () => {
    if (!product) return;
    if (product.contact_only) {
      window.location.href = `mailto:sales@maxemployee.com?subject=${encodeURIComponent(product.name)} inquiry`;
      return;
    }
    if (!user) {
      navigate(`/login?next=/products/${product.product_id}`);
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        product_id: product.product_id,
        origin_url: window.location.origin,
        setup_assist: setupAssist && setupFee > 0,
      });
      window.location.href = data.url;
    } catch {
      toast.error(t('product_detail.checkout_error'));
      setBuying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('product_detail.loading_title')}
        description={t('product_detail.loading_desc')}
        url="/products"
      />
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 font-mono text-sm text-zinc-500">{t('product_detail.loading_text')}</div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl uppercase mb-4">{t('product_detail.not_found_title')}</h1>
        <Link to="/pricing" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-wider">
          {t('product_detail.back_to_catalog')}
        </Link>
      </div>
    </div>
  );

  const cta = product.cta_label || (product.contact_only ? t('products_page.contact_sales') : t('product_detail.buy_now'));
  const title = product.card_title || product.name;
  const isPlaybook = product.product_id === "playbook";
  const isCloud = product.product_id === "max-os-cloud";
  const isEnterprise = product.product_id === "max-os-cloud-enterprise";
  const previewItems = (product.preview || "").split(" · ").filter(Boolean);
  const setupFee = typeof product.setup_fee === "number" ? product.setup_fee : null;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />

      {/* Breadcrumb */}
      <div className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link
            to="/pricing"
            data-testid="back-to-catalog"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-amber-500 font-mono text-[10px] uppercase tracking-[0.3em]"
          >
            <ArrowLeft className="w-3 h-3" /> {t('product_detail.catalog')}
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative border-b border-zinc-900 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            {product.badge && (
              <div className="inline-block bg-amber-500 text-black px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] mb-6">
                {product.badge}
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
              // {product.product_id} · {product.billing.toUpperCase()}
            </div>
            <h1 data-testid="product-title" className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95]">
              {title}
            </h1>
            <p className="mt-6 text-xl text-zinc-300 leading-relaxed max-w-2xl">
              {product.tagline}
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-2xl">
              {product.description}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={handleBuy}
                disabled={buying}
                data-testid="hero-buy-button"
                className="group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-8 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                <span>{buying ? t('common.loading') : cta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#features"
                className="inline-flex items-center gap-3 border border-zinc-800 hover:border-zinc-500 text-white px-8 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {t('product_detail.see_inside')}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-amber-500" /> {t('product_detail.secure_checkout')}</div>
              <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-amber-500" /> {t('product_detail.instant_access')}</div>
              <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> {t('product_detail.thirty_day_support')}</div>
            </div>
          </div>

          {/* Price card */}
          <aside className="col-span-12 lg:col-span-5 lg:col-start-8">
            <div className="border border-zinc-900 bg-zinc-950 p-8 lg:sticky lg:top-24">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">{t('product_detail.pricing_label')}</div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-display text-6xl text-white">
                  {product.contact_only ? t('pricing_page.custom') : `$${Number(product.price).toFixed(0)}`}
                </span>
                {!product.contact_only && (
                  <span className="font-mono text-sm text-zinc-500 uppercase tracking-wider">
                    {product.billing}
                  </span>
                )}
              </div>
              {setupFee !== null && (
                <div className="mb-6 rounded border border-zinc-800 bg-black/40 px-4 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{t('product_detail.setup_label')}</div>
                  <div className="text-sm text-zinc-200">
                    {setupFee === 0 ? t('product_detail.starts_at_0') : `$${setupFee.toFixed(0)} onboarding`}
                  </div>
                  {isCloud && (
                    <div className="mt-1 text-xs text-zinc-500">{t('product_detail.promo_setup_note')}</div>
                  )}
                </div>
              )}
              {setupFee !== null && setupFee > 0 && (
                <div className="mb-6 rounded border border-zinc-800 bg-zinc-950/60 p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="setup-assist"
                      checked={setupAssist}
                      onChange={(e) => setSetupAssist(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-amber-500 cursor-pointer"
                    />
                    <label htmlFor="setup-assist" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                          <Wrench className="w-3.5 h-3.5 text-amber-500" />
                          {t('product_detail.setup_assist')}
                        </span>
                        <span className="font-mono text-xs text-amber-500">+${setupFee.toFixed(0)} one-time</span>
                      </div>
                      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                        {t('product_detail.setup_assist_desc')}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setShowSetupModal(true); }}
                        className="mt-2 text-[10px] uppercase tracking-[0.2em] text-amber-500 hover:text-amber-400 font-mono"
                      >
                        {t('product_detail.learn_more')}
                      </button>
                    </label>
                  </div>
                </div>
              )}
              {product.is_subscription && !product.contact_only && (
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 border border-amber-500/30 px-3 py-2 mb-6">
                  {t('product_detail.recurring_subscription')}
                </div>
              )}
              <ul className="space-y-3 mb-8">
                {(product.features || []).map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleBuy}
                disabled={buying}
                data-testid="aside-buy-button"
                className="w-full group flex items-center justify-between bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-4 font-bold uppercase tracking-wider text-xs transition-colors"
              >
                <span>{buying ? t('common.loading') : cta}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              {!user && !product.contact_only && (
                <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {t('product_detail.signin_required')}
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Benefits */}
      {product.benefits && product.benefits.length > 0 && (
        <section id="features" className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="grid grid-cols-12 gap-8 mb-12">
              <div className="col-span-12 md:col-span-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">{t('product_detail.benefits_label')}</div>
                <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">{t('product_detail.why_choose')}<br/>{title}?</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-zinc-900">
              {product.benefits.map((b, i) => (
                <div key={i} data-testid={`benefit-${i}`} className="border-r border-b border-zinc-900 p-8 flex gap-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <p className="text-zinc-200 leading-relaxed">{b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Preview */}
      {product.preview && (
        <section className="border-b border-zinc-900 bg-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">{t('product_detail.preview_label')}</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase mb-12">
              {isPlaybook ? t('product_detail.inside_course') : t('product_detail.whats_included')}
            </h2>

            {isPlaybook && previewItems.length > 1 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900">
                {previewItems.map((item, i) => (
                  <div key={i} data-testid={`preview-item-${i}`} className="bg-[#09090B] p-6 hover:bg-zinc-950 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center font-mono text-xs text-amber-500 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-display text-base uppercase leading-tight">{item}</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="bg-amber-500/10 border-l-2 border-amber-500 p-6 col-span-full mt-px">
                  <div className="flex items-center gap-3">
                    <Lock className="w-4 h-4 text-amber-500" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                      {t('product_detail.full_content_unlocks')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-zinc-900 bg-zinc-950 p-8 max-w-3xl">
                <p className="text-zinc-300 leading-relaxed text-lg">{product.preview}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Dashboard mockup — Max OS Cloud */}
      {isCloud && (
        <section className="border-b border-zinc-900 bg-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="grid grid-cols-12 gap-8 mb-12">
              <div className="col-span-12 md:col-span-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
                  {t('product_detail.dashboard_label')}
                </div>
                <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
                  {t('product_detail.command_center')}<br />{t('product_detail.live_day_one')}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-6 flex items-end">
                <p className="text-zinc-400 leading-relaxed">
                  {t('product_detail.dashboard_desc')}
                </p>
              </div>
            </div>

            {/* Mockup window */}
            <div className="border border-zinc-800 bg-[#09090B] overflow-hidden">
              {/* Title bar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-950">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-zinc-700" />
                  <div className="w-3 h-3 bg-zinc-700" />
                  <div className="w-3 h-3 bg-zinc-700" />
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
                  {t('product_detail.control_center')}
                </div>
                <div className="w-16" />
              </div>

              {/* Mockup content */}
              <div className="p-6 space-y-6">
                {/* Status row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      {t('product_detail.all_systems_operational')}
                    </span>
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600">
                    {t('product_detail.plan_growth')}
                  </div>
                </div>

                {/* Metric tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
                  {[
                    { label: t('product_detail.active_agents'), value: "4/5", icon: Bot },
                    { label: t('product_detail.compute'), value: "47%", icon: Cpu },
                    { label: t('product_detail.storage'), value: "128GB", icon: HardDrive },
                    { label: t('product_detail.tasks_today'), value: "1,247", icon: Activity },
                  ].map((m, i) => (
                    <div key={i} className="bg-[#09090B] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <m.icon className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="font-display text-xl blur-[2px] select-none">{m.value}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mt-1">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Agent list + events */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                  <div className="bg-[#09090B] p-4">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500 mb-3">
                      {t('product_detail.agents_label')}
                    </div>
                    <div className="space-y-2">
                      {[
                        { name: "Ops-Alpha", status: "active", cpu: "34%" },
                        { name: "Support-Beta", status: "active", cpu: "22%" },
                        { name: "Ingest-Delta", status: "active", cpu: "61%" },
                      ].map((a, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-900 last:border-0">
                          <div className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${a.status === "active" ? "bg-green-500" : "bg-zinc-600"}`} />
                            <span className="font-mono text-xs text-zinc-300 blur-[2px] select-none">{a.name}</span>
                          </div>
                          <span className="font-mono text-[9px] text-zinc-600">CPU {a.cpu}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#09090B] p-4">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500 mb-3">
                      {t('product_detail.events_label')}
                    </div>
                    <div className="space-y-2">
                      {[
                        { time: "01:08", event: "Batch job #4821 completed" },
                        { time: "01:02", event: "Slack integration connected" },
                        { time: "00:58", event: "Security scan passed" },
                      ].map((e, i) => (
                        <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-900 last:border-0">
                          <span className="font-mono text-[9px] text-zinc-700 shrink-0">{e.time}</span>
                          <span className="font-mono text-xs text-zinc-400 blur-[2px] select-none">{e.event}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Upgrade bar */}
                <div className="border border-amber-500/20 bg-amber-500/5 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-4 h-4 text-amber-500" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                      {t('product_detail.upgrade_enterprise')}
                    </span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-amber-500 shrink-0" />
                </div>
              </div>
            </div>

            {/* CTA under mockup */}
            <div className="mt-8 flex justify-center">
              <a
                href="/preview/max-os-cloud"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-500 transition-colors"
              >
                {t('product_detail.view_dashboard_preview')}
                <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Enterprise multi-agent preview */}
      {isEnterprise && (
        <section className="border-b border-zinc-900 bg-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="grid grid-cols-12 gap-8 mb-12">
              <div className="col-span-12 md:col-span-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
                  {t('product_detail.enterprise_ops_label')}
                </div>
                <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
                  {t('product_detail.ai_department')}<br />{t('product_detail.one_command_center')}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-6 flex items-end">
                <p className="text-zinc-400 leading-relaxed">
                  {t('product_detail.multi_agent_desc')}
                </p>
              </div>
            </div>

            <div className="border border-zinc-800 bg-[#09090B] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-950">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-zinc-700" />
                  <div className="w-3 h-3 bg-zinc-700" />
                  <div className="w-3 h-3 bg-zinc-700" />
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
                  {t('product_detail.enterprise_command_center')}
                </div>
                <div className="w-16" />
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      {t('product_detail.enterprise_online')}
                    </span>
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600">
                    {t('product_detail.plan_enterprise')}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
                  {[
                    { label: t('product_detail.planner'), value: "Active", icon: Workflow },
                    { label: t('product_detail.executors'), value: "8", icon: Bot },
                    { label: t('product_detail.reviews'), value: "24/day", icon: Check },
                    { label: t('product_detail.escalations'), value: "2", icon: ShieldCheck },
                  ].map((m, i) => (
                    <div key={i} className="bg-[#09090B] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <m.icon className="w-4 h-4 text-amber-500" />
                      </div>
                      <div className="font-display text-xl blur-[1px] select-none">{m.value}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mt-1">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
                  {[
                    { name: t('product_detail.planner'), note: "allocates tasks and priorities" },
                    { name: t('product_detail.executor'), note: "runs the operational queue" },
                    { name: t('product_detail.reviewer'), note: "checks output before release" },
                  ].map((a, i) => (
                    <div key={i} className="bg-[#09090B] p-4">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500 mb-3">// {a.name}</div>
                      <div className="font-display text-xl uppercase mb-2">{a.name}</div>
                      <p className="text-sm text-zinc-400 leading-relaxed">{a.note}</p>
                    </div>
                  ))}
                </div>

                <div className="border border-amber-500/20 bg-amber-500/5 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-4 h-4 text-amber-500" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                      {t('product_detail.setup_includes')}
                    </span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-amber-500 shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA strip */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">{t('product_detail.ready_label')}</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mb-4">
            {t('product_detail.get_now_1')} <span className="text-amber-500">{title}</span> {t('product_detail.get_now_2')}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-10">
            {t('product_detail.instant_access')}. {product.is_subscription ? t('product_detail.cancel_anytime') : t('product_detail.lifetime_access')}. {setupFee !== null && setupFee > 0 ? `${t('product_detail.setup_from')} $${setupFee.toFixed(0)}.` : ""}
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button
              onClick={handleBuy}
              disabled={buying}
              className="group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-10 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              <span>{buying ? t('common.loading') : cta}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-3 border border-zinc-800 hover:border-zinc-500 text-white px-10 py-5 font-bold uppercase tracking-wider text-sm transition-colors"
            >
              {t('product_detail.compare_plans')}
            </Link>
          </div>
        </div>
      </section>

      {/* Setup Assist Learn More Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowSetupModal(false)}>
          <div className="relative w-full max-w-lg bg-[#09090B] border border-zinc-800 p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowSetupModal(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <Wrench className="w-5 h-5 text-amber-500" />
              <h3 className="font-display text-2xl uppercase">{t('product_detail.setup_assist')}</h3>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              {t('product_detail.setup_assist_desc')}
            </p>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-3">{t('product_detail.whats_included_label')}</div>
            <ul className="space-y-3 mb-6">
              {[
                t('product_detail.setup_item_1'),
                t('product_detail.setup_item_2'),
                t('product_detail.setup_item_3'),
                t('product_detail.setup_item_4'),
                t('product_detail.setup_item_5'),
              ].map((item, i) => (
                <li key={i} className="flex gap-3 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-zinc-900 pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs text-zinc-400">{t('product_detail.timeline')}</span>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                {t('product_detail.setup_yourself')}
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}